import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Grid, GridColumn as Column, GridItemChangeEvent, GridRowDoubleClickEvent, GridToolbar, GridCustomCellProps, GridCustomHeaderCellProps } from '@progress/kendo-react-grid';
import { Checkbox, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import LoadingPanel from 'components/LoadingPanel';
import SimpleDatePicker from '../../components/SimpleDatePicker';
import useAlert from '../../components/useAlert';
import { fetchApi } from '../../services/api';
import { IDName } from '../../types/idname';
import { JsonResponse } from 'TypeGen/json-response';
import { HeaderThElement } from '@progress/kendo-react-data-tools';

type Props = {
  CarrierID: number;
  CloseDialog: () => void;
};

type CarrierPayRateMasterSummary = {
  CarrierName: string;
  CarrierPayRateMasters: CarrierPayRateMasterViewModel[];
  PayRateLinehaulList: IDName[];
  PayRateAccessorialList: IDName[];
}

type CarrierPayRateMasterViewModel = {

  PayRateMasterID: number;
  ModifiedByFullName: string;
  ModifiedDateTime: Date;
  StartDate: Date;
  EndDate: Date;

  PayRateLinehaulID: number;
  PayRateLinehaulName: string;
  PayRateAccessorialID: number;
  PayRateAccessorialName: string;

  SupercededByFullName: string;
  Comment: string; //Instruction

  inEdit?: boolean | string;
  isNew?: boolean;
  Supercede?: boolean;
  IsExpired: boolean;
};

const LastModifiedByCell = (props: GridCustomCellProps) => {
    if (!props.field) return null;

    const dataItem: CarrierPayRateMasterViewModel = props.dataItem;
    return (
      <td {...props.tdProps}>
        <span>{dayjs.utc(dataItem.ModifiedDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm")} - {dataItem.ModifiedByFullName}</span>
      </td>
    );
};

const PayRateTableSelectionHeaderCell = (props: GridCustomHeaderCellProps) => {
  return <HeaderThElement {...props.thProps}>
    Pay Rate Linehaul<br />
    Pay Rate Accessorial
  </HeaderThElement>;
};

const InstructionTypeCell = (props: InstructionTypeCellProps) => {
  if (!props.field)
    return null;

  const dataItem: CarrierPayRateMasterViewModel = props.dataItem;

  const handleChange = (e: DropDownListChangeEvent, field: string) => {
    if (props.onChange) {

      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: field, //props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.ID,
      });
    }
  };

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit ? <DropDownList
        className="my-1"
        data={props.PayRateLinehaulList}
        textField="Name"
        dataItemKey="ID"
        value={props.PayRateLinehaulList.find((x: IDName) => x.ID == dataItem.PayRateLinehaulID)}
        onChange={(e) => handleChange(e, 'PayRateLinehaulID')}
      /> :
        <div>{dataItem.PayRateLinehaulName}</div>
      }

      {dataItem.inEdit ? <DropDownList
        className="my-1"
        data={props.PayRateAccessorialList}
        textField="Name"
        dataItemKey="ID"
        value={props.PayRateAccessorialList.find((x: IDName) => x.ID == dataItem.PayRateAccessorialID)}
        onChange={(e) => handleChange(e, 'PayRateAccessorialID')}
      /> :
        <div>{dataItem.PayRateAccessorialName}</div>
      }
    </td>
  );
};

const CommentCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const handleChange = (e: TextBoxChangeEvent) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value,
      });
    }
  };

  const dataItem: CarrierPayRateMasterViewModel = props.dataItem;

  return (
    <td {...props.tdProps}>
      {dataItem.inEdit ?
        <TextBox
          value={dataItem.Comment}
          onChange={handleChange}
          style={{ width: '100%' }}
        /> :
        <span>{dataItem.Comment}</span>
      }
    </td>
  );
};

interface EditCommandCellProps extends GridCustomCellProps {
  edit: (item: CarrierPayRateMasterViewModel) => void;
  add: (item: CarrierPayRateMasterViewModel) => void;
  discard: () => void;
  update: (item: CarrierPayRateMasterViewModel) => void;
  cancel: (item: CarrierPayRateMasterViewModel) => void;
  editField: string;
}

interface InstructionTypeCellProps extends GridCustomCellProps {
  PayRateLinehaulList: IDName[];
  PayRateAccessorialList: IDName[];
}

const DateCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const handleChange = (e: Date) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: null,
        value: e,
      });
    }
  };

  return (
    <td {...props.tdProps}>
      {props.dataItem.inEdit ?
        <SimpleDatePicker
          value={dayjs.utc(props.dataItem[props.field]).toDate()}
          onChange={handleChange}
        />
        :
        <div className={props.field === 'EndDate' && props.dataItem.IsExpired ? 'text-warning' : ''}>
          {dayjs.utc(props.dataItem[props.field]).tz("America/New_York").format("MM/DD/YYYY")}
        </div>
      }
    </td>
  );
};

const SetCarrierPayRateMaster = (props: Props) => {
  const [loading, setLoading] = useState(true);
  const editField: string = "inEdit";
  const [CSIDataCopy, setCSIDataCopy] = useState<CarrierPayRateMasterViewModel[]>([]);
  const [CarrierPayRateMasters, setCarrierPayRateMasters] = useState<CarrierPayRateMasterViewModel[]>([]);
  const [carrierName, setCarrierName] = useState('');
  const [payRateLinehaulList, setPayRateLinehaulList] = useState<IDName[]>([]);
  const [payRateAccessorialList, setPayRateAccessorialList] = useState<IDName[]>([]);
  const [hideExpiredPayRateStructures, setHideExpiredPayRateStructures] = useState(true);
  const { alert } = useAlert();
  const PayRateInstructionTypeCell = useCallback((cellProps: GridCustomCellProps) => (
    <InstructionTypeCell
      {...cellProps}
      PayRateLinehaulList={payRateLinehaulList}
      PayRateAccessorialList={payRateAccessorialList}
    />
  ), [payRateAccessorialList, payRateLinehaulList]);

  const EditCommandCell = (props: EditCommandCellProps) => {
    const inEdit = props.dataItem[props.editField];
    const dataItem: CarrierPayRateMasterViewModel = props.dataItem;
    const isNewItem = dataItem.isNew;

    return inEdit ? (
      <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button
          themeColor="primary"
          disabled={dataItem.StartDate == null || dataItem.EndDate == null}
          onClick={() =>
            isNewItem ? props.add(dataItem) : props.update(dataItem)
          }
        >
          {isNewItem ? "Save" : "Update"}
        </Button>
        <Button
          themeColor="secondary"
          onClick={() =>
            isNewItem ? props.discard() : props.cancel(dataItem)
          }
        >
          {isNewItem ? "Discard" : "Cancel"}
        </Button>
      </td>
    ) : (
      <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
        <Button
          themeColor="primary"
          disabled={CarrierPayRateMasters.some(x => x.inEdit === true || x.isNew === true)}
          onClick={() => props.edit(dataItem)}
        >
          Edit
        </Button>
      </td>
    );
  };

  const refresh = useCallback(() => {
    setLoading(true);

    fetchApi(`/api/Settlements/GetCarrierPayRateMasters/${props.CarrierID}`)
      .then((summary: CarrierPayRateMasterSummary) => {
        setLoading(false);

        summary.CarrierPayRateMasters.forEach(obj => {
          obj.StartDate = dayjs.utc(obj.StartDate).toDate();
          obj.EndDate = dayjs.utc(obj.EndDate).toDate();
        });

        setCarrierPayRateMasters(summary.CarrierPayRateMasters);
        setCSIDataCopy(summary.CarrierPayRateMasters);

        setCarrierName(summary.CarrierName);
        setPayRateLinehaulList(summary.PayRateLinehaulList);
        setPayRateAccessorialList(summary.PayRateAccessorialList);
      })
      .catch(async err => {
        setLoading(false);
        await alert(err);
      });
  }, [props.CarrierID, alert]);

  useEffect(() => {
    refresh();
  }, [props.CarrierID, refresh]);

  const enterEdit = (dataItem: CarrierPayRateMasterViewModel) => {
    if (dataItem.inEdit === true)
      return;

    setCarrierPayRateMasters(
      CarrierPayRateMasters.map((item) =>
        item.PayRateMasterID === dataItem.PayRateMasterID ? { ...item, inEdit: true } : item
      )
    );
  };

  const updateCarrierPayRateMasters = (dataItem: CarrierPayRateMasterViewModel) => {
    setLoading(true);
    const data = { ...dataItem }
    fetchApi(`/api/Settlements/CarrierPayRateMaster/${props.CarrierID}`, data, 'POST')
      .then(async (response: JsonResponse) => {
        if (response.Success === false) {
          setLoading(false);
          await alert(`Error: ${response.ErrorMessage}`);
        }
        else
          refresh();
      })
      .catch(async () => {
        await alert('An error occurred while saving.');
        setLoading(false);
      });
  }

  const add = (dataItem: CarrierPayRateMasterViewModel) => {
    updateCarrierPayRateMasters(dataItem);
  };

  const update = (dataItem: CarrierPayRateMasterViewModel) => {
    updateCarrierPayRateMasters(dataItem);
  };

  const discard = () => {
    const newData = [...CarrierPayRateMasters];
    newData.splice(0, 1);
    setCarrierPayRateMasters(newData);
  };

  const cancel = (dataItem: CarrierPayRateMasterViewModel) => {
    const originalItem = CSIDataCopy.find(
      (p) => p.PayRateMasterID === dataItem.PayRateMasterID
    );
    const newData = CarrierPayRateMasters.map((item) =>
      item.PayRateMasterID === originalItem.PayRateMasterID ? originalItem : item
    );

    setCarrierPayRateMasters(newData);
  };

  const generateId = () =>
    CarrierPayRateMasters.reduce((acc, current) => Math.max(acc, current.PayRateMasterID), 0) + 1;

  const addNew = () => {
    const newDataItem = {
      inEdit: true, PayRateMasterID: generateId(), isNew: true,
      Comment: '', PayRateLinehaulID: 0, PayRateAccessorialID: 0,
      StartDate: dayjs.utc().tz("America/New_York").startOf('day').toDate(),
      EndDate: dayjs.tz("12/31/2999 00:00", "America/New_York").toDate(),
      IsExpired: false
    } as CarrierPayRateMasterViewModel;
    setCarrierPayRateMasters([newDataItem, ...CarrierPayRateMasters]);
  };

  const itemChange = (event: GridItemChangeEvent) => {
    const dataItem = event.dataItem as CarrierPayRateMasterViewModel;
    const newData = CarrierPayRateMasters.map((item) =>
      item.PayRateMasterID === dataItem.PayRateMasterID
        ? { ...item, [event.field || ""]: event.value }
        : item
    );

    setCarrierPayRateMasters(newData);
  }

  const MyEditCommandCell = (props: GridCustomCellProps) => (
    <EditCommandCell {...props}
      edit={enterEdit}
      add={add}
      discard={discard}
      update={update}
      cancel={cancel}
      editField={editField}
    />
  );

  return (<>
    <Dialog title={`Set Carrier ${carrierName} Pay Rate Structure`} onClose={props.CloseDialog} className="dialog-w1140">
      <div className="container-fluid">
        {loading && <LoadingPanel />}
        <Grid
          data={hideExpiredPayRateStructures ? CarrierPayRateMasters.filter(x => x.IsExpired === false) : CarrierPayRateMasters}
          dataItemKey="PayRateMasterID"
          onItemChange={itemChange}
          edit={Object.fromEntries(CarrierPayRateMasters.filter(x => x.inEdit).map(x => [x.PayRateMasterID, true]))}
          onRowDoubleClick={(e: GridRowDoubleClickEvent) => { enterEdit(e.dataItem); }}
        >
          <GridToolbar>
            <Button
              themeColor="primary"
              disabled={CarrierPayRateMasters.some(x => x.inEdit === true || x.isNew === true)}
              title="Add new"
              onClick={addNew}
            >
              Add new
            </Button>
            <span>
              <Checkbox
                className="ml-2"
                value={hideExpiredPayRateStructures}
                onChange={(e) => setHideExpiredPayRateStructures(e.value)}
                label={"Hide Expired Pay Rate Structures"}
              />
            </span>
          </GridToolbar>
          <Column field="ModifiedByFullName" title="Modified By" cells={{ data: LastModifiedByCell }} />
          <Column field="StartDate" title="Effective From" cells={{ data: DateCell }} width={175} />
          <Column field="EndDate" title="Effective To" cells={{ data: DateCell }} width={175} />
          <Column field="PayRateLinehaulID" title="Entity Type" cells={{
            data: PayRateInstructionTypeCell,
            headerCell: PayRateTableSelectionHeaderCell
          }} />
          <Column field="Comment" title="Comment" cells={{ data: CommentCell }} />
          <Column cells={{ data: MyEditCommandCell }} width={150} />
        </Grid>
      </div>
    </Dialog>
  </>
  )
}

export default SetCarrierPayRateMaster;
